import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";

import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";

import { patch } from "../helpers/HTTP";

import type { PaymentSource } from "../types";

async function setPaymentSourceRequest(
  workspaceId: string,
  subscriptionId: string,
  paymentSourceId: string,
) {
  await patch(
    `workspaces/${workspaceId}/subscriptions/${subscriptionId}/payment-source`,
    {
      payment_source_id: paymentSourceId,
    },
  );
}
async function setPaymentSourceWithoutWorkspaceRequest(
  paymentSourceId: string,
) {
  await patch(`subscriptions/payment-source`, {
    payment_source_id: paymentSourceId,
  });
}

export default function usePaymentSourceMutations() {
  const queryClient = useQueryClient();
  const selectedWorkspace = useSelectedWorkspaceContext();

  const { mutateAsync: setPaymentSource, isPending: isSettingPaymentSource } =
    useMutation({
      onMutate: ({ paymentSource, subscriptionId }) => {
        const queryKey = [subscriptionId, "primaryPaymentSource"];

        const originalPaymentSource =
          queryClient.getQueryData<PaymentSource>(queryKey);

        queryClient.setQueryData(queryKey, paymentSource);

        return originalPaymentSource;
      },
      mutationFn: ({
        paymentSource,
        subscriptionId,
      }: {
        paymentSource: PaymentSource;
        subscriptionId: string;
      }) =>
        selectedWorkspace
          ? setPaymentSourceRequest(
              selectedWorkspace.id,
              subscriptionId,
              paymentSource.id,
            )
          : setPaymentSourceWithoutWorkspaceRequest(paymentSource.id),
      onError: (_, { subscriptionId }, originalPaymentSource) => {
        queryClient.setQueryData(
          [subscriptionId, "primaryPaymentSource"],

          // if original payment source is undefined, the query update is ignored. this ensures that it updates
          originalPaymentSource || null,
        );
        toast.error("Error updating payment source");
      },
      onSuccess: () => toast.success("Payment source updated"),
    });

  return {
    setPaymentSource,
    isSettingPaymentSource,
  };
}
